import { useId, useLayoutEffect } from '@finn-no/troika-react-utils';
import classNames from 'classnames';
import React, { forwardRef, useState, useRef, useMemo, useContext, createContext } from 'react';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var ariaControlsPrefix = function ariaControlsPrefix(id) {
  return "expansion-panel-ac" + id;
};

var ariaLabelledByPrefix = function ariaLabelledByPrefix(id) {
  return "expansion-panel__al" + id;
};

var ExpansionPanelState = /*#__PURE__*/createContext({
  expanded: false,
  setExpanded: function setExpanded(expanded) {
    return expanded;
  }
});
var ExpansionPanelAria = /*#__PURE__*/createContext({
  'aria-controls': undefined,
  'aria-labelledby': undefined
});
var ExpansionPanelContainer = /*#__PURE__*/forwardRef(function (_ref, forwardRef) {
  var children = _ref.children,
      className = _ref.className,
      _ref$expanded = _ref.expanded,
      defaultExpanded = _ref$expanded === void 0 ? false : _ref$expanded,
      bleed = _ref.bleed,
      bright = _ref.bright,
      onCollapse = _ref.onCollapse,
      onExpand = _ref.onExpand,
      ariaControls = _ref['aria-controls'],
      ariaLabelledBy = _ref['aria-labelledby'],
      rest = _objectWithoutPropertiesLoose(_ref, ["children", "className", "expanded", "bleed", "bright", "onCollapse", "onExpand", "aria-controls", "aria-labelledby"]);

  var _useState = useState(defaultExpanded),
      expanded = _useState[0],
      setExpanded = _useState[1];

  var prevExpanded = useRef(false);
  var autoId = useId();
  var ariaContext = useMemo(function () {
    return {
      'aria-controls': ariaControls || ariaControlsPrefix(autoId),
      'aria-labelledby': ariaLabelledBy || ariaLabelledByPrefix(autoId)
    };
  }, [autoId, ariaControls, ariaLabelledBy]);
  var stateContext = useMemo(function () {
    return {
      expanded: expanded,
      setExpanded: setExpanded
    };
  }, [expanded]); // useLayoutEffect is used to avoid flashing and jank should multiple panels be changing their state at once, this ensures it's in sync

  useLayoutEffect(function () {
    if (prevExpanded.current === expanded) {
      return;
    }

    prevExpanded.current = expanded;

    if (expanded && onExpand) {
      var _event = new Event('expand', {
        cancelable: true
      });

      onExpand(_event);
      if (_event.defaultPrevented) setExpanded(false);
    }

    if (!expanded && onCollapse) {
      var _event2 = new Event('collapse', {
        cancelable: true
      });

      onCollapse(_event2);
      if (_event2.defaultPrevented) setExpanded(true);
    }
  }, [expanded, onCollapse, onExpand, prevExpanded]); // Sync any changes to the expanded prop to the expanded state

  useLayoutEffect(function () {
    return setExpanded(defaultExpanded);
  }, [defaultExpanded]);
  return /*#__PURE__*/React.createElement("div", _extends({}, rest, {
    ref: forwardRef,
    className: classNames('expansion-panel', {
      'expansion-panel--open': expanded,
      'expansion-panel--bleed': bleed,
      'expansion-panel--bright': bright
    }, className)
  }), /*#__PURE__*/React.createElement(ExpansionPanelAria.Provider, {
    value: ariaContext
  }, /*#__PURE__*/React.createElement(ExpansionPanelState.Provider, {
    value: stateContext
  }, children)));
});
var ExpansionPanelButton = /*#__PURE__*/forwardRef(function (_ref2, forwardRef) {
  var children = _ref2.children,
      className = _ref2.className,
      _onClick = _ref2.onClick,
      props = _objectWithoutPropertiesLoose(_ref2, ["children", "className", "onClick"]);

  var _useContext = useContext(ExpansionPanelState),
      expanded = _useContext.expanded,
      setExpanded = _useContext.setExpanded;

  var _useContext2 = useContext(ExpansionPanelAria),
      ariaLabelledBy = _useContext2['aria-labelledby'],
      ariaControls = _useContext2['aria-controls'];

  return /*#__PURE__*/React.createElement("button", _extends({}, props, {
    ref: forwardRef,
    "aria-controls": ariaControls,
    "aria-expanded": expanded,
    className: classNames('expansion-panel__clicktarget', className),
    id: ariaLabelledBy,
    onClick: function onClick(event) {
      setExpanded(function (expanded) {
        return !expanded;
      });
      _onClick == null ? void 0 : _onClick(event);
    },
    type: "button"
  }), /*#__PURE__*/React.createElement("span", {
    className: "expansion-panel__summary"
  }, children));
});
var ExpansionPanelDetails = /*#__PURE__*/forwardRef(function (_ref3, forwardRef) {
  var children = _ref3.children,
      className = _ref3.className,
      props = _objectWithoutPropertiesLoose(_ref3, ["children", "className"]);

  var _useContext3 = useContext(ExpansionPanelAria),
      ariaLabelledBy = _useContext3['aria-labelledby'],
      ariaControls = _useContext3['aria-controls'];

  return /*#__PURE__*/React.createElement("div", _extends({}, props, {
    ref: forwardRef,
    "aria-labelledby": ariaLabelledBy,
    className: classNames('expansion-panel__details', className),
    id: ariaControls,
    role: "region"
  }), children);
});
var ExpansionPanelHeading = /*#__PURE__*/forwardRef(function (_ref4, forwardRef) {
  var children = _ref4.children,
      _ref4$headingLevel = _ref4.headingLevel,
      headingLevel = _ref4$headingLevel === void 0 ? 2 : _ref4$headingLevel,
      className = _ref4.className,
      rest = _objectWithoutPropertiesLoose(_ref4, ["children", "headingLevel", "className"]);

  var validHeadingLevel = Math.max(1, Math.min(6, headingLevel));
  var Heading = "h" + validHeadingLevel;
  return /*#__PURE__*/React.createElement(Heading, _extends({}, rest, {
    className: classNames('expansion-panel__header', className),
    ref: forwardRef
  }), children);
});
var ExpansionPanel = /*#__PURE__*/forwardRef(function (_ref5, forwardRef) {
  var children = _ref5.children,
      title = _ref5.title,
      headingLevel = _ref5.headingLevel,
      rest = _objectWithoutPropertiesLoose(_ref5, ["children", "title", "headingLevel"]);

  return /*#__PURE__*/React.createElement(ExpansionPanelContainer, _extends({}, rest, {
    ref: forwardRef
  }), /*#__PURE__*/React.createElement(ExpansionPanelHeading, {
    headingLevel: headingLevel
  }, /*#__PURE__*/React.createElement(ExpansionPanelButton, null, title)), /*#__PURE__*/React.createElement(ExpansionPanelDetails, null, children));
});
ExpansionPanelContainer.displayName = "ExpansionPanelContainer";
ExpansionPanelButton.displayName = "ExpansionPanelButton";
ExpansionPanelDetails.displayName = "ExpansionPanelDetails";
ExpansionPanelHeading.displayName = "ExpansionPanelHeading";
ExpansionPanel.displayName = "ExpansionPanel";

export { ExpansionPanel, ExpansionPanelButton, ExpansionPanelContainer, ExpansionPanelDetails, ExpansionPanelHeading };
//# sourceMappingURL=index.es.js.map
